/**
 * Inline UUID v7 generator.
 *
 * UUID v7 (RFC 9562) is a time-ordered UUID: the most-significant 48 bits are
 * a Unix epoch millisecond timestamp, followed by 12 bits of sub-millisecond
 * randomness and 62 bits of pure randomness. This makes the value:
 *
 *  - **monotonic-ish** — lexicographic sort ≈ creation order, which keeps the
 *    outbox drain order stable and database indexes append-friendly;
 *  - **collision-resistant** — 74 bits of entropy per millisecond;
 *  - **idempotency-friendly** — usable directly as a `client_event_uuid` so a
 *    replayed POST is recognised server-side without an extra key.
 *
 * No dependency on the global `crypto.randomUUID` (v4 only) — we need v7.
 * Falls back to `Math.random` when the Web Crypto API is unavailable (older
 * webviews / SSR), which is acceptable because the timestamp prefix already
 * supplies practical uniqueness for an outbox key.
 */

import {
	validateUuidv7Timestamp,
	validateUuidv7TimestampString,
} from './validate-input'

const HEX = '0123456789abcdef'

function fillRandom(out: Uint8Array, start: number, end: number): void {
	const cryptoObj = (globalThis as { crypto?: Crypto }).crypto
	if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
		const slice = out.subarray(start, end)
		cryptoObj.getRandomValues(slice)
		return
	}
	for (let i = start; i < end; i++) {
		out[i] = Math.floor(Math.random() * 256)
	}
}

/**
 * Generate a fresh UUID v7 string (`xxxxxxxx-xxxx-7xxx-Yxxx-xxxxxxxxxxxx`).
 *
 * @param now - Override the timestamp (ms since epoch). Defaults to `Date.now()`.
 *              Exposed for deterministic tests.
 */
export function uuidv7(now: number = Date.now()): string {
	// Wave 10 zod-mandate — runtime validation at the public boundary.
	validateUuidv7Timestamp(now)
	const bytes = new Uint8Array(16)

	// 48-bit big-endian millisecond timestamp into bytes 0..5.
	const ts = Math.max(0, Math.floor(now))
	bytes[0] = (ts / 0x10000000000) & 0xff
	bytes[1] = (ts / 0x100000000) & 0xff
	bytes[2] = (ts / 0x1000000) & 0xff
	bytes[3] = (ts / 0x10000) & 0xff
	bytes[4] = (ts / 0x100) & 0xff
	bytes[5] = ts & 0xff

	// Random fill for the remaining 10 bytes.
	fillRandom(bytes, 6, 16)

	// Version 7: top nibble of byte 6.
	bytes[6] = (bytes[6]! & 0x0f) | 0x70
	// Variant 10xx: top two bits of byte 8.
	bytes[8] = (bytes[8]! & 0x3f) | 0x80

	let s = ''
	for (let i = 0; i < 16; i++) {
		const b = bytes[i]!
		s += HEX[b >> 4]
		s += HEX[b & 0x0f]
		if (i === 3 || i === 5 || i === 7 || i === 9) s += '-'
	}
	return s
}

/**
 * Extract the embedded millisecond timestamp from a UUID v7 string. Useful for
 * dedupe windows / observability. Returns `NaN` for non-v7 input.
 */
export function uuidv7Timestamp(uuid: string): number {
	// Wave 10 zod-mandate — runtime validation at the public boundary.
	validateUuidv7TimestampString(uuid)
	const hex = uuid.replace(/-/g, '')
	if (hex.length !== 32) return Number.NaN
	if (hex[12] !== '7') return Number.NaN
	return Number.parseInt(hex.slice(0, 12), 16)
}

/**
 * True if `value` is a syntactically valid UUID v7.
 */
export function isUuidv7(value: unknown): value is string {
	return (
		typeof value === 'string' &&
		/^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
	)
}
